import invariant from 'tiny-invariant';
import type { DoneInvokeEvent, MachineOptions } from 'xstate';
import { assign } from 'xstate';

import { Mode } from 'src/modules/designStudio/providers';
import { ToastType } from 'src/shared/components/Toast/types';
import {
  client as apolloClient,
  defaultLocalStorageAdapter,
} from 'src/shared/graphql';
import type { Job, Jwt } from 'src/shared/graphql/generated';
import { mutations, queries } from 'src/shared/graphql/requests';
import { ChannelEnum, openChannel } from 'src/shared/utils/webviewChannel';

import { sharedOptions } from '../sharedOptions';

import { offlineToast } from './helpers';
import type {
  Context,
  Event,
  FetchedJobEvent,
  SetProfileAttributesEvent,
  ShowErrorEvent,
  UpdateOnlineStatusEvent,
} from './types';

const NETWORK_CHECK_INTERVAL = 15000;

const options = {
  actions: {
    setProfileAttributes: assign((_, evt: Event) => {
      const event = evt as SetProfileAttributesEvent;
      const { partnerId, userId, orgId } = event.data;
      return { partnerId, userId, orgId };
    }),
    updateJobMetadata: assign((_, evt: Event) => {
      const event = evt as FetchedJobEvent;
      const job = event.data;
      const context: Partial<Context> = {};

      context.canRequestRendering = job?.canRequestRendering;

      if (job?.captureRequest?.requestingUser?.orgs?.results.length) {
        const captureRequestOrg =
          job?.captureRequest?.requestingUser?.orgs?.results[0];
        context.requestingOrgName =
          captureRequestOrg?.customerDisplayName ?? undefined;
        context.isConnectJob = !!captureRequestOrg?.id;
      }

      const renderings = job?.hiDefRenderings;
      if (renderings) context.hasRenderings = renderings.length > 0;

      return context;
    }),
    logError: sharedOptions.logError,
    assignBuildingLoaded: assign<Context, Event>({
      buildingLoaded: true,
    }),
    assignLoadingAnimationDone: assign<Context, Event>({
      loadingAnimationDone: true,
    }),
    showOfflineMessage: (ctx, e) => {
      const payload = e as ShowErrorEvent;

      ctx.addToast?.({
        title: payload.data.message,
        type: ToastType.PASSIVE,
        onPress: ctx.removeToast,
      });
      return assign({ isOnline: payload.data.isOnline });
    },
    assignOnlineStatus: assign((_, event) => ({
      isOnline: (event as UpdateOnlineStatusEvent).isOnline,
    })),
  },
  guards: {
    jobNotFound: (_ctx, evt: any) => {
      const event = evt as DoneInvokeEvent<Job | undefined>;
      return event.data === undefined;
    },
    finishedLoading: ctx =>
      (ctx.mode === Mode.PREVIEW || !!ctx.loadingAnimationDone) &&
      !!ctx.buildingLoaded,
    isGuestMode: ctx => ctx.mode === Mode.GUEST,
    isShareMode: ctx => ctx.mode === Mode.SHARE,
    isPreviewMode: ctx => ctx.mode === Mode.PREVIEW,
    isInteractiveRenderingMode: ctx => ctx.mode === Mode.INTERACTIVE_RENDERING,
  },
  services: {
    fetchProfile: async ctx => {
      /**
       * The GraphQL query to fetch the user profile requires the user to be authenticated. If they are not
       * design studio will redirect to the login screen. The user is not always logged in during
       * GUEST, PREVIEW, or SHARE mode.
       */
      if (
        ctx.mode === Mode.GUEST ||
        ctx.mode === Mode.PREVIEW ||
        ctx.mode === Mode.SHARE
      ) {
        return {};
      }

      if (ctx.jobId) {
        const partnerId = await sharedOptions.fetchPartnerIdForJob(ctx.jobId);
        const profileData = await queries.profile(apolloClient, {});
        const user = profileData?.data.profile.user;
        return { partnerId, userId: user.id, orgId: user.orgs[0].id };
      }

      return {};
    },
    fetchHoverToken: async () => {
      try {
        const channel = openChannel<unknown, Jwt>(
          ChannelEnum.SHARE_HOVER_AUTHORIZATION,
        );
        channel.send();
        const jwt = await channel.receive;
        await defaultLocalStorageAdapter.setAccessToken(jwt.accessToken);
      } catch (e) {
        await mutations.authenticateWithCookie(apolloClient);
      }
    },
    fetchJob: async ctx => {
      const modesWithoutJob = [
        Mode.GUEST,
        Mode.PREVIEW,
        Mode.INTERACTIVE_RENDERING,
      ];
      if (modesWithoutJob.includes(ctx.mode)) {
        return {};
      }
      invariant(ctx.jobId, 'Missing jobId - That is sus dude');
      const jobResponse = await queries.getJob(apolloClient, { id: ctx.jobId });
      return jobResponse.data.jobs.results[0];
    },
    setupNetworkListeners:
      ({ translator }) =>
      callback => {
        let listenerId: NodeJS.Timeout;
        const offlineMessage = translator('app.offline');

        window?.addEventListener('offline', () => {
          // trigger toast as soon as we go offline
          offlineToast(callback, offlineMessage);

          callback({ type: 'UPDATE_ONLINE_STATUS', isOnline: false });

          listenerId = setInterval(() => {
            offlineToast(callback, offlineMessage);
          }, NETWORK_CHECK_INTERVAL);
        });

        window?.addEventListener('online', () => {
          callback({ type: 'UPDATE_ONLINE_STATUS', isOnline: true });
          if (listenerId) clearInterval(listenerId);
        });

        return () => listenerId && clearInterval(listenerId);
      },
  },
} as const satisfies Pick<
  MachineOptions<Context, Event>,
  'actions' | 'guards' | 'services'
>;

export { options };
